import MainLayout from 'src/pages/private';
import { FC, useState, useEffect } from 'react';
import SearchBar from 'src/components/InputFields/Searchbar';
import {
  PrimaryActionButton,
  AddItemIcon,
  AddItemText,
  ClickableContainer,
  RemoveItemIcon,
  FlexOverflowContainer,
  FlexContainer,
  FlexColumnContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import ProductionMappingGrid from 'src/components/Grids/ProductionMappingGrid';
import {
  errorMessages,
  productionMappingDialogTitles,
  productionMappingDialogSubtitles,
  productionMappingFormTitles,
} from 'src/utils/properties.utils';
import { useFormik } from 'formik';
import TextField from 'src/components/InputFields/TextField';
import ProductSelect from 'src/components/InputFields/ProductPicker';
import RawMaterialSelect from 'src/components/InputFields/RawMaterialPicker';
import { addProductionMapping } from 'src/services/productionMapping.service';
import ProductionDialog from 'src/components/DialogBox/ProductionBox/ProductionDialog';
import {
  FlexColumnBox,
  BorderedSectionBox,
  SectionTitleTypography,
  ItemRowBox,
  ItemFieldBox,
  ItemFieldAlignedBox,
  RemoveItemBox,
} from 'src/components/DialogBox/POBox/POBox.styles';
import { productionMappingValidation } from 'src/utils/validationScheme.utils';
import { getBasicMaterials } from 'src/services/material.service';

const ProductionMapping: FC = () => {
  interface RawMaterial {
    id: string;
    name: string;
  }

  const { showSnackbar } = StyledSnackbar();

  const defaultProduction = {
    rawMaterialId: '',
    quantity: '',
    id: Date.now().toString(),
  };

  const [loading, setLoading] = useState(false);
  const [mappingAdded, setMappingAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([{ ...defaultProduction }]);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);

  useEffect(() => {
    if (open) {
      loadRawMaterials();
    }
  }, [open]);

  const loadRawMaterials = async () => {
    try {
      const materials = await getBasicMaterials();
      setRawMaterials(materials);
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  const formik = useFormik({
    initialValues: {
      productId: '',
    },
    validationSchema: productionMappingValidation(items),
    enableReinitialize: true,
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);

    const details = items
      .map((item) => {
        const rawMaterialId = values[`rawMaterialId-${item.id}`];
        const quantity = values[`quantity-${item.id}`];

        return { rawMaterialId, quantity: Number(quantity) };
      })
      .filter((item) => item.rawMaterialId && item.quantity > 0);

    if (details.length === 0) {
      showSnackbar(errorMessages.PLEASE_ADD_AT_LEAST_ONE_VALID_ITEM, 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await addProductionMapping({
        productId: values.productId,
        details,
      });
      showSnackbar(response.message, 'success');
      setMappingAdded(!mappingAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setItems([{ ...defaultProduction }]);
    setOpen(false);
  };

  const handleAddItem = () => {
    setItems([...items, { ...defaultProduction }]);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const selectedMaterialIds = items
    .map((item) => (formik.values as any)[`rawMaterialId-${item.id}`])
    .filter(Boolean);

  const calculateDialogHeight = () => {
    const baseHeight = 580;
    const itemHeight = 120;
    const additionalItems = items.length > 1 ? items.length - 1 : 0;
    return baseHeight + additionalItems * itemHeight;
  };

  return (
    <MainLayout
      title="Production Mapping"
      searchBar={
        <SearchBar
          width="300px"
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search Production Mappings by name"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="100px"
          onClick={handleClickOpen}
        >
          <i className="bx bx-plus" /> Add
        </PrimaryActionButton>
      }
    >
      <ProductionMappingGrid state={mappingAdded} searchTerm={searchTerm} />
      <FlexContainer>
        <FlexOverflowContainer>
          <ProductionDialog
            width="840px"
            height={`${calculateDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title={productionMappingDialogTitles.ADD}
            subtitle={productionMappingDialogSubtitles.ADD}
            onSubmit={formik.handleSubmit}
            buttonDisabled={loading}
            buttonLoading={loading}
            buttonLabel="Save"
          >
            <FlexColumnContainer mt={1.5}>
              <FlexColumnBox>
                <ProductSelect
                  color="var(--subtext)"
                  width="630px"
                  id="productId"
                  label="Product"
                  formikProps={formik}
                  isUnmapped
                />
              </FlexColumnBox>
              <BorderedSectionBox>
                <SectionTitleTypography>
                  Raw Material Details
                </SectionTitleTypography>
                {items.map((item, index) => (
                  <ItemRowBox key={item.id}>
                    <ItemFieldBox>
                      <RawMaterialSelect
                        showLabel={index === 0}
                        color="var(--subtext)"
                        width="300px"
                        id={`rawMaterialId-${item.id}`}
                        label="Raw Material"
                        formikProps={formik}
                        selectedIds={selectedMaterialIds}
                        rawMaterialsList={rawMaterials}
                      />
                    </ItemFieldBox>
                    <ItemFieldAlignedBox>
                      <TextField
                        showLabel={index === 0}
                        color="var(--subtext)"
                        width="300px"
                        id={`quantity-${item.id}`}
                        label="Quantity"
                        numberOnly
                        formikProps={formik}
                      />
                    </ItemFieldAlignedBox>
                    {items.length > 1 && (
                      <RemoveItemBox>
                        <RemoveItemIcon
                          component="i"
                          className="bx bx-minus-circle 1"
                          onClick={() => handleRemoveItem(item.id)}
                        />
                      </RemoveItemBox>
                    )}
                  </ItemRowBox>
                ))}
              </BorderedSectionBox>
              <ClickableContainer
                width="130px"
                mt={1}
                mb={2}
                onClick={handleAddItem}
              >
                <AddItemIcon component="i" className="bx bx-plus" />
                <AddItemText>Add Item</AddItemText>
              </ClickableContainer>
            </FlexColumnContainer>
          </ProductionDialog>
        </FlexOverflowContainer>
      </FlexContainer>
    </MainLayout>
  );
};

export default ProductionMapping;
